import React, { useState } from "react";
import { Layers, DollarSign, Eye, Link2, Loader2, Copy, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";
import { usePdfPreview } from "@/components/PdfPreviewDialog";

// One PDF for every coverage level of a program (shared commission, one class guide).
export function CombinedRateCard({ title, products, testId = "combined-rate-card" }) {
  const [selected, setSelected] = useState(products.map((p) => p.id));
  const [pageMarkup, setPageMarkup] = useState(0);
  const [dealerName, setDealerName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const { preview, dialog } = usePdfPreview();

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = selected.length === products.length;
  const body = () => {
    const ids = products.filter((p) => selected.includes(p.id)).map((p) => p.id);
    return { product_id: ids[0], product_ids: ids, page_markup: Number(pageMarkup) || 0, row_overrides: {}, dealer_name: dealerName || null, notes: notes || null };
  };

  const download = async () => {
    if (selected.length === 0) { toast.error("Pick at least one coverage level"); return; }
    setBusy(true);
    try {
      await preview({ fetcher: () => api.post("/portfolio/rate-card/pdf", body(), { responseType: "blob" }), filename: `AWG_Rate_Card_${title.replace(/\s+/g, "_")}_All_Levels.pdf`, title: `Rate Card · ${title} — all levels` });
    } finally { setBusy(false); }
  };
  const createShare = async () => {
    if (selected.length === 0) { toast.error("Pick at least one coverage level"); return; }
    try { const { data } = await api.post("/portfolio/rate-card/share", body()); setShare(data); }
    catch { toast.error("Could not create link"); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(share.share_url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Copy failed"); } };

  return (
    <Card className="border-[#D4AF37] bg-[#D4AF37]/5 rounded-sm p-5" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-[#0B132B] flex items-center justify-center shrink-0"><Layers size={16} className="text-[#D4AF37]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#D4AF37]">Combined rate card</div>
          <div className="font-bold text-[#0B132B] text-base">{title} — all coverage levels</div>
          <p className="text-xs text-[#6C757D] mt-0.5">Tick the levels to include, set one commission for every page, and generate a single PDF. Surcharges and the class guide print once at the end.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`${testId}-levels`}>
        <button type="button" onClick={() => setSelected(allOn ? [] : products.map((p) => p.id))} className="px-2.5 py-1.5 rounded-sm border text-[10px] uppercase tracking-[0.14em] font-bold border-[#0B132B] bg-[#0B132B] text-white" data-testid={`${testId}-select-all`}>
          {allOn ? "Clear all" : "Select all"}
        </button>
        {products.map((p) => {
          const on = selected.includes(p.id);
          return (
            <label key={p.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border cursor-pointer text-xs font-semibold ${on ? "border-[#0B132B] bg-white text-[#0B132B]" : "border-[#E9ECEF] bg-white/60 text-[#6C757D]"}`} data-testid={`${testId}-level-${p.slug}`}>
              <Checkbox checked={on} onCheckedChange={() => toggle(p.id)} className="h-3.5 w-3.5" />
              {p.name.replace(/\s*\(New Car\)\s*$/i, "")}
            </label>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Commission — all pages</Label>
          <div className="relative mt-1">
            <DollarSign size={13} className="absolute left-2 top-2.5 text-[#6C757D]" />
            <Input type="number" min={0} step="1" value={pageMarkup} onChange={(e) => setPageMarkup(e.target.value === "" ? 0 : Number(e.target.value))} className="rounded-sm h-9 pl-7 text-sm" data-testid={`${testId}-markup`} />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Dealer name (optional)</Label>
          <Input value={dealerName} onChange={(e) => setDealerName(e.target.value)} placeholder="e.g. Smith Motors" className="rounded-sm h-9 text-sm mt-1" data-testid={`${testId}-dealer`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Notes (optional)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Terms, disclaimers…" className="rounded-sm h-9 text-sm mt-1" data-testid={`${testId}-notes`} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" onClick={download} disabled={busy} className="h-9 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90" data-testid={`${testId}-pdf-btn`}>
          {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Generating…</> : <><Eye size={12} className="mr-1.5" /> Preview & Download ({selected.length} level{selected.length === 1 ? "" : "s"})</>}
        </Button>
        <Button size="sm" variant="outline" onClick={createShare} className="h-9 rounded-sm border-[#0B132B]/20" data-testid={`${testId}-share-btn`}>
          <Link2 size={12} className="mr-1.5" /> Create dealer link
        </Button>
      </div>
      <Dialog open={!!share} onOpenChange={(o) => !o && setShare(null)}>
        <DialogContent data-testid={`${testId}-share-dialog`}>
          <DialogHeader><DialogTitle>Public rate card link</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 p-2 bg-[#F8F9FA] rounded-sm border border-[#E9ECEF]">
            <input readOnly value={share?.share_url || ""} className="flex-1 bg-transparent text-xs font-mono outline-none" data-testid={`${testId}-share-url`} />
            <Button size="sm" variant="outline" onClick={copy} className="h-8 rounded-sm">{copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShare(null)} className="rounded-sm">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {dialog}
    </Card>
  );
}
